import { useAtomValue } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { fetchMyProducts } from '@/api/products/getProducts';
import FilledBadge from '@/components/Common/Badge/FilledBadge';
import DraftListingBox from '@/components/Common/DraftListingBox';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import MyProductListSkeleton from '@/components/Common/Skeleton/MyProductListSkeleton';
import StatusBadge from '@/components/Common/StatusBadge';
import MenuHeader from '@/components/Header/MenuHeader';
import { EmptyTruckIcon, LockClosedOutlineIcon, PencilIcon, SellOutlineIcon } from '@/components/Icon';
import MoreMenu from '@/components/Menu/MoreMenu';
import ProductStatus from '@/components/More/ProductStatus';
import ProductType from '@/components/More/ProductType';
import ProductTypeChangeButton from '@/components/More/ProductTypeChangeButton';
import MyProductPrice from '@/components/Products/MyProductPrice';
import SaleTipBox from '@/components/Products/Sale/SaleTipBox';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { BEFORE_SALE, ORIGIN_DATA_REGISTER, SALE, WAITING } from '@/const/productStatus';
import { PRODUCT_TYPE_SPEED } from '@/const/products';
import { IMAGE_BASE_URL } from '@/const/url';
import { useToastContext } from '@/contexts/Common/ToastContext';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { memberAtom } from '@/store/member';
import { useResetProductsForm } from '@/store/products';
import { canModifyProduct, getProductType, isShowTypeChangeButtonAndMoreMenu } from '@/utils/Products/productUtils';
import { formatNumber, formatYYYYMMDD } from '@/utils/common';
import { Button, Fab, MenuItem, Switch, SxProps, Zoom, useTheme } from '@mui/material';

const SpeedConsultingOverlay = () => (
  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-[10px] px-4 text-white bg-black/50">
    <div className="absolute top-2 left-4">
      <FilledBadge
        text="진행중"
        textColor="text-gray-0"
        bgColor="primary-10"
        rounded="rounded-[8px]"
        fontWeight="font-semibold"
        fontSize="text-[13px]"
      />
    </div>
    <div className="flex items-center gap-2">
      <LockClosedOutlineIcon width={22} height={22} />
      <span className="text-[20px] sm280:text-[17px] font-bold">견적상담 중</span>
    </div>
    <span className="text-[15px] sm280:text-[13px] font-medium text-center break-keep">
      상담 중에는 매물을 수정 할 수 없습니다.
    </span>
  </div>
);

const MyProducts = () => {
  const navigate = useNavigate();
  const memberAtomData = useAtomValue(memberAtom);
  const isDealerType = memberAtomData?.type ? memberAtomData.type?.code !== 'NORMAL' : false;
  const resetProductsForm = useResetProductsForm();
  const [myProducts, setMyProducts] = useState<ProductDetailResponse[]>([]);
  const [menuStates, setMenuStates] = useState<boolean[]>([]);
  const [isNone, setIsNone] = useState<boolean>(false);
  const { showToast } = useToastContext();
  const deleteId = useRef<number>();
  const theme = useTheme();

  const [isScrolling, setIsScrolling] = useState(false);

  const [isShowPopup, setIsShowPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState<string | undefined>('');
  const [isConfirmModal, setIsConfirmModal] = useState(false);

  const [isShowAlertModal, setIsShowAlertModal] = useState(false);
  const [alertModalContent, setAlertModalContent] = useState({
    reason: '',
    modifiedDate: '',
  });
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<{ [key: number]: boolean }>({});

  const [editingPriceProductId, setEditingPriceProductId] = useState<number | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>('');
  const [isSavingPrice, setIsSavingPrice] = useState(false);

  const [isConfirmModalConfig, setIsConfirmModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const { data, isFetching, isLoading } = useQuery(['getMyProducts'], () => fetchMyProducts(), {
    onSuccess: (responseData) => {
      setMyProducts(responseData);
      if (responseData.length === 0) {
        setIsNone(true);
      }
      setMenuStates(responseData.map(() => false));
    },
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'success', 'bottom');
      setIsNone(true);
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const fetchDetail = (product: ProductDetailResponse) => {
    // 바텀시트가 열려있는 경우 클릭 무시
    if (isBottomSheetOpen[product.id]) {
      return;
    }
    if (product.status.code !== ORIGIN_DATA_REGISTER && product.status.code !== BEFORE_SALE) {
      navigate('/products/' + product.id, { state: { isPublicDetail: false } });
    }
  };

  useEffect(() => {
    setIsNone(myProducts?.length === 0);
  }, [myProducts]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const onClickDelete = (item: ProductDetailResponse) => {
    if (item) {
      // setIsShowPopup(true);
      setIsConfirmModal(true);
      setIsConfirmModalConfig({
        title: '차량을 정말 삭제하시겠어요?',
        content: '차량을 정말 삭제하시겠어요?',
        leftButtonLabel: '취소',
        rightButtonLabel: '삭제하기',
        onLeftButtonClick: () => {
          setIsShowPopup(false);
        },
        onRightButtonClick: () => {
          deleteProduct();
        },
      });

      setPopupTitle(item.truckNumber);
      deleteId.current = item.id;
    }
  };

  const deleteProduct = () => {
    apiManager
      .delete(`/api/v1/products/${deleteId.current}`)
      .then((response) => {
        if (response) {
          setMyProducts((prev) => prev.filter((item) => item.id !== deleteId.current));
        }
        showToast('차량이 삭제되었어요.', 'success', 'bottom');
        setIsConfirmModal(false);
        // setIsShowPopup(false);
      })
      .catch((error) => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });
  };

  const isSpeedDraft = (item: ProductDetailResponse) =>
    item.status.code === BEFORE_SALE && item.type?.code === PRODUCT_TYPE_SPEED;

  const hasNonDraftProduct = myProducts.some((item) => item.status.code !== BEFORE_SALE);

  const isShowDivider =
    hasNonDraftProduct && myProducts.some((item) => item.status.code === BEFORE_SALE && !isSpeedDraft(item));

  const isShowSpeedDraftDivider = hasNonDraftProduct && myProducts.some(isSpeedDraft);

  const onClickMore = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    resetProductsForm();
    sessionStorage.setItem('entryPoint', location.pathname);
  }, []);

  const handleClick = (e: React.MouseEvent, data: any) => {
    e.stopPropagation();
    setIsShowAlertModal(true);
    setAlertModalContent({
      reason: data?.reason,
      modifiedDate: formatYYYYMMDD(data?.modifiedDate),
    });
  };

  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/[^\d]/g, '');
    const limited = onlyDigits.slice(0, 5);
    setEditingPriceValue(limited);
  };

  const openPriceEditor = (e: React.MouseEvent, item: ProductDetailResponse) => {
    e.stopPropagation();
    if (item.status.code !== SALE || isDealerType) return;
    setEditingPriceProductId(item.id);
    setEditingPriceValue(item.price ? String(item.price) : '');
  };

  const cancelPriceEditor = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingPriceProductId(null);
    setEditingPriceValue('');
    setIsSavingPrice(false);
  };

  const executeSavePrice = async (productId: number) => {
    if (isSavingPrice) return;

    const cleaned = (editingPriceValue || '').replace(/[^\d]/g, '');
    const nextPrice = Number(cleaned);

    if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
      showToast('판매 가격을 올바르게 입력해 주세요.', 'error', 'bottom');
      return;
    }

    const product = myProducts.find((p) => p.id === productId);
    const approvedPrice = product?.price ?? 0;
    if (product?.approvalStatusList?.at(-1)?.status?.code === 'APPROVAL' && nextPrice > approvedPrice) {
      showToast('가격은 그 이상으로 수정이 불가능합니다.', 'error', 'bottom');
      return;
    }

    try {
      setIsSavingPrice(true);
      await newApiManager.patch(`/api/v1/products/${productId}`, { price: nextPrice });
      setMyProducts((prev) => prev.map((p) => (p.id !== productId ? p : { ...p, price: nextPrice })));
      if (nextPrice <= approvedPrice - 50) {
        setIsConfirmModal(true);
        setIsConfirmModalConfig({
          title: '차량 판매 금액 수정 완료',
          content: (
            <>
              <div className="mb-4">
                50만원 이상 수정되어 <br />
                해당 차량이 상단에 노출돼요.
              </div>
            </>
          ),
          leftButtonLabel: '',
          rightButtonLabel: '확인',
          onLeftButtonClick: () => {},
          onRightButtonClick: () => {
            setIsConfirmModal(false);
          },
        });
      } else {
        showToast('판매 가격이 변경되었어요.', 'success', 'bottom');
      }

      cancelPriceEditor();
    } catch (error) {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      setIsSavingPrice(false);
    }
  };

  const savePrice = async (e: React.MouseEvent, productId: number) => {
    e.stopPropagation();
    await executeSavePrice(productId);
  };

  const onPriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, productId: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void executeSavePrice(productId);
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelPriceEditor();
    }
  };

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  const fabStyle = {
    position: 'absolute',
    bottom: 16,
    right: 16,
  };

  const fabs = [
    {
      key: 1,
      variant: 'circular' as 'circular',
      className: `bg-blueGradient-blue-to-blue flex`,
      sx: fabStyle as SxProps,
      icon: <SellOutlineIcon color={colors.gray[0]} width={30} height={30} />,
    },
    {
      key: 2,
      variant: 'extended' as 'extended',
      className: `bg-blueGradient-blue-to-blue flex`,
      sx: fabStyle as SxProps,
      icon: (
        <>
          <span>
            <SellOutlineIcon color={colors.gray[0]} width={30} height={30} />
          </span>
          <span className="text-white text-base font-semibold ml-[10px]">차량 판매하러 가기</span>
        </>
      ),
    },
  ];

  const onClickSellCar = () => {
    navigate('/sell-car');
  };

  return (
    <>
      <DefaultHelmet page="sale" />
      <MenuHeader title="내차관리" isMain={true}></MenuHeader>
      <div className="w-full pt-[60px] min-h-[calc(100vh-60px)] pb-32">
        {isFetching || isLoading ? (
          <MyProductListSkeleton></MyProductListSkeleton>
        ) : isNone ? (
          <div className="flex flex-col items-center justify-center w-full h-full px-4">
            <EmptyTruckIcon color={colors.gray[4]} width={120} height={120}></EmptyTruckIcon>
            <span className="text-gray-7 text-lg mt-[40px]">차량 내역이 없습니다.</span>
          </div>
        ) : (
          <section className="flex">
            <ul className="grid w-full grid-cols-1 gap-2">
              {myProducts && (
                <>
                  {myProducts.map((item) =>
                    item.status.code === 'BEFORE_SALE' && !isSpeedDraft(item) ? (
                      <div key={item.id} className="px-4">
                        <DraftListingBox data={item} onClickDelete={onClickDelete} updateList={setMyProducts} />
                      </div>
                    ) : null,
                  )}
                </>
              )}

              {isShowDivider && <div className="w-full h-2 bg-gray-1"></div>}

              <div className="mt-4 px-4">
                <SaleTipBox />
              </div>

              {myProducts.map((item, index) =>
                item.status.code !== 'BEFORE_SALE' ? (
                  <div key={item.id} className="relative px-4">
                    {item.type?.code === PRODUCT_TYPE_SPEED && <SpeedConsultingOverlay />}
                    <li
                      className={`relative flex justify-between items-center py-4 ${
                        index !== 0 ? 'border-t border-gray-4' : ''
                      }`}
                    >
                      <div className="flex justify-between flex-1" onClick={() => fetchDetail(item)}>
                        <div className="h-[100px] w-[100px] min-w-[80px] min-h-[80px]">
                          <img
                            className="object-cover w-full h-full bg-cover rounded-lg"
                            src={
                              item.productsImage?.frontSideImageUrl
                                ? item.productsImage.frontSideImageUrl
                                : `${IMAGE_BASE_URL}/car_none.png`
                            }
                            alt="트럭 사진"
                          />
                        </div>
                        <div className="pl-3 pr-6 flex-1 min-w-[160px]">
                          {item.type?.code !== PRODUCT_TYPE_SPEED && (
                            <div className="flex flex-wrap gap-[6px]">
                              {(item.approvalStatusList?.length === 0 ||
                                item.approvalStatusList?.at(-1)?.status?.code === 'APPROVAL') && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <ProductStatus
                                    status={item.status}
                                    id={item.id}
                                    path="products"
                                    price={item.price ?? undefined}
                                    setProductList={setMyProducts}
                                    selectBoxBoolean={canModifyProduct(item.type?.code)}
                                    onBottomSheetStateChange={(isOpen) => {
                                      setIsBottomSheetOpen((prev) => ({
                                        ...prev,
                                        [item.id]: isOpen,
                                      }));

                                      if (isOpen && editingPriceProductId === item.id) {
                                        cancelPriceEditor();
                                      }
                                    }}
                                  ></ProductStatus>
                                </div>
                              )}

                              {item.approvalStatusList?.length === 0 && (
                                <ProductType
                                  type={getProductType(item.type, item.salesType, item.statusOfSpeedProduct)}
                                />
                              )}
                              {item.approvalStatusList?.at(-1)?.status?.code === 'REJECT' ? (
                                <FilledBadge
                                  textColor="text-gray-6"
                                  bgColor="gray-3"
                                  rounded="rounded-[8px]"
                                  fontWeight="font-semibold"
                                  text="승인 반려"
                                />
                              ) : item.approvalStatusList?.at(-1)?.status?.code === 'WAITING' ||
                                item.approvalStatusList?.at(-1)?.status?.code === 'APPROVAL' ? (
                                <StatusBadge
                                  label={item.approvalStatusList?.at(-1)?.status?.desc}
                                  textColor="text-green-0"
                                  bgColor="bg-gray-1"
                                />
                              ) : null}
                            </div>
                          )}

                          <dd className="pt-2 text-base font-medium text-gray-8">{item.truckNumber}</dd>
                          <dd className="flex items-center w-full">
                            {item.approvalStatusList?.at(-1)?.status?.code !== WAITING &&
                            editingPriceProductId === item.id ? (
                              <div
                                className="mt-1 mb-0 sm450:mb-1 flex flex-col gap-2 sm450:flex-row sm450:items-center sm450:justify-start w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="relative w-full sm450:w-[170px]">
                                  <input
                                    className="w-full px-2 py-1 text-lg font-semibold text-gray-9 border border-gray-3 rounded-md"
                                    value={editingPriceValue ? formatNumber(editingPriceValue, true) : ''}
                                    onChange={handlePriceInputChange}
                                    onKeyDown={(e) => onPriceKeyDown(e, item.id)}
                                    inputMode="numeric"
                                    placeholder="판매 가격"
                                    autoFocus
                                  />
                                  <span className="absolute top-0 right-2 flex justify-center items-center h-full">
                                    만원
                                  </span>
                                </div>
                                <div className="flex smax450:w-full gap-2 sm450:max-w-[200px] sm450:flex-1 break-keep">
                                  <button
                                    className="w-full h-[36px] smax450:w-full smax450:h-fit smax450:mb-1 px-2 py-1 text-sm font-semibold rounded-md bg-primary-10 text-white"
                                    onClick={(e) => savePrice(e, item.id)}
                                    disabled={isSavingPrice}
                                  >
                                    저장
                                  </button>
                                  <button
                                    className="w-full h-[36px] smax450:w-full smax450:h-fit px-2 py-1 text-sm font-semibold rounded-md bg-gray-3 text-gray-8"
                                    onClick={(e) => cancelPriceEditor(e)}
                                    disabled={isSavingPrice}
                                  >
                                    취소
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-lg font-semibold text-gray-9 flex items-center gap-2 flex-col sm400:flex-row w-full">
                                <MyProductPrice product={item} className="w-full text-left sm400:w-fit" />
                                {item.status.code === SALE &&
                                  !isDealerType &&
                                  canModifyProduct(item.type?.code) &&
                                  item.approvalStatusList?.at(-1)?.status?.code !== WAITING && (
                                    <button
                                      onClick={(e) => openPriceEditor(e, item)}
                                      aria-label="판매 가격 수정"
                                      className="w-full mb-1 sm400:mb-0 text-primary-10 text-sm font-semibold bg-secondaryBlue-0 px-2 py-1 rounded-md border border-primary-10 sm400:w-fit"
                                    >
                                      가격 수정
                                    </button>
                                  )}
                              </span>
                            )}
                          </dd>
                          {item.approvalStatusList?.at(-1)?.status?.code === 'REJECT' &&
                            item.approvalStatusList?.at(-1)?.reason && (
                              <p
                                className="font-medium underline underline-offset-2 text-gray-7 text-[14px]"
                                onClick={(e) => handleClick(e, item.approvalStatusList?.at(-1))}
                              >
                                반려 사유 확인
                              </p>
                            )}
                          {isShowTypeChangeButtonAndMoreMenu(item.salesType?.code) && (
                            <ProductTypeChangeButton
                              id={item.id}
                              type={item.type}
                              status={item.status}
                              setProductList={setMyProducts}
                              statusOfSpeedProduct={item.statusOfSpeedProduct}
                            ></ProductTypeChangeButton>
                          )}
                        </div>
                        {isShowTypeChangeButtonAndMoreMenu(item.salesType?.code) && (
                          <div className="absolute right-2 top-[60px] z-20" onClick={onClickMore}>
                            <MoreMenu
                              id={item.id}
                              status={item.status.code}
                              productsType={item.type?.code}
                              price={Number(item.price)}
                              setProductList={setMyProducts}
                              confirmTitle={item.truckNumber}
                            />
                          </div>
                        )}
                      </div>
                    </li>
                  </div>
                ) : null,
              )}

              {isShowSpeedDraftDivider && <div className="w-full h-2 bg-gray-1"></div>}

              {myProducts.map((item) =>
                isSpeedDraft(item) ? (
                  <div key={item.id} className="relative px-4">
                    <SpeedConsultingOverlay />
                    <DraftListingBox
                      data={item}
                      onClickDelete={onClickDelete}
                      updateList={setMyProducts}
                      isXMarkVisible={false}
                      isContinueBtnVisible={false}
                    />
                  </div>
                ) : null,
              )}
            </ul>
          </section>
        )}
      </div>

      {/* <ConfirmModal
        isOpen={isShowPopup}
        title={popupTitle}
        content="차량을 정말 삭제하시겠어요?"
        rightButtonLabel="삭제하기"
        onLeftButtonClick={() => setIsShowPopup(false)}
        onRightButtonClick={() => deleteProduct()}
      /> */}

      <ConfirmModal
        isOpen={isConfirmModal}
        title={isConfirmModalConfig.title}
        content={isConfirmModalConfig.content}
        leftButtonLabel={isConfirmModalConfig.leftButtonLabel}
        rightButtonLabel={isConfirmModalConfig.rightButtonLabel}
        onLeftButtonClick={isConfirmModalConfig.onLeftButtonClick}
        onRightButtonClick={isConfirmModalConfig.onRightButtonClick}
      />

      <AlertModal
        isOpen={isShowAlertModal}
        title={'반려 사유'}
        content={
          <>
            {alertModalContent?.reason}
            <br />
            {alertModalContent?.modifiedDate}
          </>
        }
        onClose={() => {
          setIsShowAlertModal(false);
        }}
      />

      <div className="flex items-center justify-end fixed w-full mx-auto my-0 pr-4 pt-2 pb-4 bottom-[64px] left-0 right-0 z-20 max-w-[720px] min-w-[280px]">
        {fabs.map((fab, index) => (
          <Zoom
            key={fab.key}
            in={isScrolling ? index % 2 === 0 : index % 2 !== 0}
            timeout={transitionDuration}
            style={{
              transitionDelay: `${isScrolling ? transitionDuration.exit : 0}ms`,
            }}
            mountOnEnter
            unmountOnExit
          >
            <Fab sx={fab.sx} className={fab.className} variant={fab.variant} onClick={onClickSellCar}>
              {fab.icon}
            </Fab>
          </Zoom>
        ))}
      </div>
    </>
  );
};

export default MyProducts;
